import React, { FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from '../../../../shared/components';
import SvgImage from '../../../../shared/components/svg-image';
import { i18n } from '../../../../shared/helpers/i18n';
import css from './index.css';

interface CopyUrlButtonProps {
    wasCopied: boolean;
    setWasCopied: (v: boolean) => void;
    onCopyUrl: () => void;
}

const CopyUrlButton: FunctionComponent<CopyUrlButtonProps> = ({ wasCopied, setWasCopied, onCopyUrl }) => {
    const [isHovered, setHovered] = useState(false);
    const [wasAutoCopied, setWasAutoCopied] = useState(wasCopied);

    useEffect(() => {
        const handleWindowBlur = () => {
            setWasAutoCopied(false);
            setWasCopied(false);
        };
        window.addEventListener('blur', handleWindowBlur);
        return () => window.removeEventListener('blur', handleWindowBlur);
    }, [setWasCopied, setWasAutoCopied]);

    const handleClick = useCallback(() => {
        onCopyUrl();
        setHovered(false);
        setWasCopied(true);
        setWasAutoCopied(false);
    }, [onCopyUrl, setWasCopied]);

    const buttonIcon = useMemo(() => {
        return <SvgImage imageName={wasCopied && !isHovered ? 'checkmark' : 'chain'} className={css.CopyButtonIcon} />;
    }, [wasCopied, isHovered]);

    const handleMouseEnter = useCallback(() => setHovered(true), []);
    const handleMouseLeave = useCallback(() => setHovered(false), []);

    const buttonTitle = useMemo(() => {
        if (!wasCopied) {
            return i18n('common', 'copy-link');
        } else {
            if (wasAutoCopied) {
                if (!isHovered) {
                    return i18n('call', 'share-link-copy-button-success');
                } else {
                    return i18n('call', 'share-link-copy-button');
                }
            } else {
                if (!isHovered) {
                    return i18n('call', 'link-successfully-copied');
                } else {
                    return i18n('common', 'copy-link');
                }
            }
        }
    }, [wasCopied, isHovered, wasAutoCopied]);

    return (
        <Button
            type="submit"
            view="brand"
            size="md"
            onMouseEnter={handleMouseEnter}
            onHoverEnd={handleMouseLeave}
            iconLeft={buttonIcon}
            className={css.CopyButton}
            onClick={handleClick}
        >
            {buttonTitle}
        </Button>
    );
};

export default CopyUrlButton;
